import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ParentStackParamList } from "../../navigation/types";
import { childrenApi } from "../../api/children";
import { useAuthStore } from "../../state/authStore";
import { Mascot } from "../../components/Mascot";
import { SelectField } from "../../components/SelectField";
import { colors, fontFamily, mascotColors, radii } from "../../theme";
import { CONTENT_CATEGORIES } from "../../data/categories";
import {
  MONTH_LABELS,
  ageFromBirthday,
  birthYearOptions,
} from "../../utils/birthday";

type Props = NativeStackScreenProps<ParentStackParamList, "EditChild">;

// Edit child — deliberately the same form as Add child (name, birthday, mascot
// colour, interests), just pre-filled.
//
// It used to offer name + age only, with blank fields and "Leave blank to keep"
// placeholders. That meant a parent couldn't see what they were changing, and
// three things chosen at creation — birthday, mascot colour, interests — could
// never be changed again. Writing `age` directly also left the stored
// `birthday` behind, so the two disagreed from then on; this screen always
// writes the birthday and lets the backend derive the age.
export default function EditChildScreen({ navigation, route }: Props) {
  const { childId } = route.params;
  const { accessToken } = useAuthStore();
  const insets = useSafeAreaInsets();

  const [name, setName] = useState("");
  const [birthMonth, setBirthMonth] = useState<number | null>(null);
  const [birthYear, setBirthYear] = useState<number | null>(null);
  const [color, setColor] = useState<string>(mascotColors[0]);
  const [picked, setPicked] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [focused, setFocused] = useState<string | null>(null);

  const birthday =
    birthMonth !== null && birthYear !== null
      ? new Date(birthYear, birthMonth, 1)
      : null;
  const age = birthday ? ageFromBirthday(birthday) : null;
  const years = useMemo(() => birthYearOptions(birthYear), [birthYear]);

  // Prefill everything: the child record plus their parental controls, which is
  // where the interests chosen at creation actually live.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const kids = await childrenApi.list(accessToken);
        if (cancelled) return;
        const child = kids.find((k) => k.id === childId);
        if (child) {
          setName(child.name ?? "");
          if (child.color) setColor(child.color);
          if (child.birthday) {
            const d = new Date(child.birthday);
            if (!Number.isNaN(d.getTime())) {
              setBirthMonth(d.getMonth());
              setBirthYear(d.getFullYear());
            }
          } else if (child.age != null) {
            // Older profiles predate the birthday field. Derive a birth year
            // from the stored age so the picker isn't blank; the month stays
            // unset so it's obvious the parent should confirm it.
            setBirthYear(new Date().getFullYear() - child.age);
          }
        }
        // Best-effort — a kid with no controls row yet just starts with no
        // interests selected.
        try {
          const controls = await childrenApi.getControls(childId, accessToken);
          if (!cancelled && controls?.categories) setPicked(controls.categories);
        } catch {
          /* no controls yet */
        }
      } catch {
        /* leave the form empty rather than blocking the screen */
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, childId]);

  const toggleInterest = (i: string) =>
    setPicked((cur) =>
      cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i],
    );

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert("Please enter a name");
      return;
    }
    if (!birthday) {
      Alert.alert("Please pick a birthday (month and year)");
      return;
    }
    setLoading(true);
    try {
      await childrenApi.update(
        childId,
        {
          name: name.trim(),
          age: ageFromBirthday(birthday),
          birthday: birthday.toISOString(),
          color,
        },
        accessToken!,
      );
      // Interests live on Controls.categories — same as Add child. Best-effort:
      // the profile itself is already saved.
      try {
        await childrenApi.updateControls(
          childId,
          { categories: picked },
          accessToken!,
        );
      } catch {
        /* non-fatal */
      }
      navigation.goBack();
    } catch {
      Alert.alert("Couldn't save", "Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Alert.alert(
      "Delete profile?",
      "This removes the child and their watch history. This can't be undone.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await childrenApi.remove(childId, accessToken!);
            } finally {
              navigation.navigate("ParentHome");
            }
          },
        },
      ],
    );
  };

  return (
    <View style={styles.root}>
      <ScrollView
        contentContainerStyle={[
          styles.scroll,
          { paddingTop: insets.top + 8, paddingBottom: insets.bottom + 24 },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Top bar — Cancel sits top-left, the standard place to back out. */}
        <View style={styles.topBar}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={styles.cancelTop}>Cancel</Text>
          </TouchableOpacity>
        </View>

        {/* Live preview — mascot in the chosen colour, like Add child. */}
        <View style={styles.previewCard}>
          <View style={styles.previewMascotRing}>
            <View style={styles.previewMascotInner}>
              <Mascot mood="happy" color={color} size={94} />
            </View>
          </View>
          <Text style={styles.previewName}>
            {name || (hydrating ? "Loading…" : "Child")}
          </Text>
          <Text style={styles.previewAge}>
            {age !== null ? `${age} years` : "Age not set"}
          </Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.label}>NAME</Text>
          <TextInput
            style={[styles.input, focused === "name" && styles.inputFocused]}
            placeholder={hydrating ? "Loading…" : "Child's name"}
            placeholderTextColor={colors.ink3}
            value={name}
            onChangeText={setName}
            onFocus={() => setFocused("name")}
            onBlur={() => setFocused(null)}
            returnKeyType="next"
          />

          <Text style={[styles.label, styles.labelSpaced]}>BIRTHDAY</Text>
          <View style={styles.selectRow}>
            <View style={styles.selectHalf}>
              <SelectField
                placeholder="Month"
                value={birthMonth !== null ? MONTH_LABELS[birthMonth] : null}
                options={MONTH_LABELS.map((m, i) => ({ label: m, value: i }))}
                onSelect={setBirthMonth}
              />
            </View>
            <View style={styles.selectHalf}>
              <SelectField
                placeholder="Year"
                value={birthYear !== null ? String(birthYear) : null}
                options={years.map((y) => ({ label: String(y), value: y }))}
                onSelect={setBirthYear}
              />
            </View>
          </View>

          <Text style={[styles.label, styles.labelSpaced]}>MASCOT COLOR</Text>
          <View style={styles.colorRow}>
            {mascotColors.map((c) => {
              const active = c === color;
              return (
                <TouchableOpacity
                  key={c}
                  onPress={() => setColor(c)}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  style={[
                    styles.colorSwatch,
                    {
                      backgroundColor: c,
                      borderColor: active ? colors.ink : "transparent",
                    },
                  ]}
                />
              );
            })}
          </View>

          <Text style={[styles.label, styles.labelSpaced]}>
            WHAT DO THEY LOVE?
          </Text>
          <View style={styles.interestRow}>
            {CONTENT_CATEGORIES.map((i) => {
              const on = picked.includes(i);
              return (
                <TouchableOpacity
                  key={i}
                  onPress={() => toggleInterest(i)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityState={{ selected: on }}
                  style={[
                    styles.interestChip,
                    {
                      backgroundColor: on ? colors.ink : "transparent",
                      borderColor: on ? colors.ink : colors.lineStrong,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.interestText,
                      { color: on ? "#fff" : colors.ink2 },
                    ]}
                  >
                    {i}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Primary action */}
        <TouchableOpacity
          style={[styles.saveBtn, loading && styles.saveBtnDisabled]}
          onPress={handleSave}
          disabled={loading}
          activeOpacity={0.85}
        >
          <Text style={styles.saveText}>
            {loading ? "Saving…" : "Save changes"}
          </Text>
        </TouchableOpacity>

        {/* Destructive action — visually quieter so it's not a mis-tap magnet. */}
        <TouchableOpacity
          style={styles.deleteBtn}
          onPress={handleDelete}
          activeOpacity={0.85}
        >
          <Text style={styles.deleteText}>Delete profile</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bgWarm,
  },
  scroll: {
    flexGrow: 1,
    paddingHorizontal: 24,
  },
  topBar: {
    height: 40,
    justifyContent: "center",
  },
  cancelTop: {
    fontFamily,
    fontSize: 16,
    fontWeight: "700",
    color: colors.ink2,
  },

  // Preview card — mirrors Add child so the two screens read as one flow.
  previewCard: {
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    paddingVertical: 20,
    alignItems: "center",
    marginBottom: 18,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.06,
    shadowRadius: 18,
    elevation: 3,
  },
  previewMascotRing: {
    width: 110,
    height: 110,
    borderRadius: 55,
    backgroundColor: colors.bgWarm,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  previewMascotInner: {
    marginTop: 14,
  },
  previewName: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    marginTop: 8,
  },
  previewAge: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: colors.ink2,
    marginTop: 2,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    padding: 20,
    marginBottom: 22,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.06,
    shadowRadius: 22,
    elevation: 3,
  },
  label: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.6,
    color: colors.ink3,
    marginBottom: 6,
  },
  labelSpaced: {
    marginTop: 16,
  },
  input: {
    width: "100%",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    backgroundColor: colors.surface,
    borderRadius: radii.md,
    fontFamily,
    fontSize: 15,
    fontWeight: "500",
    color: colors.ink,
  },
  inputFocused: {
    borderColor: colors.primary,
  },

  selectRow: {
    flexDirection: "row",
    gap: 12,
  },
  selectHalf: {
    flex: 1,
  },

  colorRow: {
    flexDirection: "row",
    gap: 12,
  },
  colorSwatch: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 3,
  },

  interestRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  interestChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: radii.pill,
    borderWidth: 1.5,
  },
  interestText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
  },

  saveBtn: {
    width: "100%",
    paddingVertical: 16,
    borderRadius: radii.md,
    backgroundColor: colors.primary,
    alignItems: "center",
    borderBottomWidth: 3,
    borderBottomColor: "rgba(0,0,0,0.18)",
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 5,
    marginBottom: 14,
  },
  saveBtnDisabled: {
    opacity: 0.7,
  },
  saveText: {
    fontFamily,
    fontSize: 16,
    fontWeight: "900",
    color: "#fff",
    letterSpacing: 0.2,
  },
  deleteBtn: {
    width: "100%",
    paddingVertical: 14,
    borderRadius: radii.md,
    backgroundColor: "transparent",
    alignItems: "center",
  },
  deleteText: {
    fontFamily,
    fontSize: 15,
    fontWeight: "800",
    color: "#E5484D",
  },
});
